/* eslint no-multi-spaces: 0 */
/* eslint key-spacing    : 0 */

import DataTypes from './data-types'
import { goto, drag, view } from './cells/actions'
import MoreActions from './MoreActions'
import checkboxProxy from './cells/checkboxProxy'

/**
 * Filter constructor
 * @method mixin   - flattens `filterModel`
 * @method request - returns request filter parameters
 *
 */
export const Filter = {
  /**
   * @param   {Object} model  - `filterModel`
   * @param   {String} search - quick filter text
   * @returns {Object} filter - flattened (key, val) filter
   */
  mixin(model, search, screen) {
    const filter = {}

    model && Object.keys(model).forEach(key => {
      model[key].filter
      ? model[key].type
        ? filter[model[key].type] = model[key].filter
        : filter[key] = model[key].filter
      : null
    })

    filter.searchTerm = search
    filter.Screen = screen
    return filter
  },

  mixout(string, model) {
    if (!string) return null

    const gridApi = this.gridOptions.columnApi
    const filter = JSON.parse(string)
    const filterModel = {}

    Object.keys(filter).forEach(key => {
      if (model[key] || model[`*${key}`]) {
        filterModel[key] = { filter: filter[key] }
        gridApi.getColumn(key).colDef.auto = true
      }
    })

    return filterModel
  },

  /**
   * @param   {Object} params - grid state `params`
   * @returns {Object} params - request parameters
   */
  request(params) {
    // const DEFAULT_SORT_COLUMN = 'CreatedDateStr'
    // const DEFAULT_SORT_DIRECTION = 'DESC'

    let sortColumn = params.sortModel.length ? params.sortModel[0].colId : null
    let sortDirection = params.sortModel.length ? params.sortModel[0].sort.toUpperCase() : null

    let columnsToLoad

    if (this.onlyVisibleColumns) {
      let dependencyColumns = []

      let idColumn = this.gridOptions.api.columnController.allDisplayedColumns
        .map(column => column.colDef.uid)
        .filter(column => column)

      let visibleColumns = this.gridOptions.api.columnController.allDisplayedColumns
        .filter(column => !column.colDef.action)
        .map(column => {
          column.colDef.dataColumn && dependencyColumns.push(column.colDef.dataColumn.split(','))
          return column.colId
        })

      columnsToLoad = [].concat(
        ...idColumn,
        ...visibleColumns,
        ...dependencyColumns
      )

      columnsToLoad = columnsToLoad
        .filter((value, i, self) => self.indexOf(value) === i)
        .join(',')
    }

    return {
      sort                  : sortColumn,
      dir                   : sortDirection,
      start                 : params.startRow || 0,
      limit                 : 100,
      isLiveGrid            : true,
      getJustVisibleColumns : columnsToLoad && this.onlyVisibleColumns,
      filter                : JSON.stringify(
        Filter.mixin(
          params.filterModel,
          params.search,
          params.screen
        )
      ),
      columnsToLoad
    }
  }
}

/**
 * Column model constructor
 * @method mixin  - maps model to `columnDefs`
 * @method header - mixins filters into column model
 *
 */
export const Model = {
  /**
   * @param   {Object} model      - backend column model
   * @returns {Array} columnModel - mapped model
   */
  mixin(model, actions, grid) {
    const columnModel = []
    let uid = null

    // drag handle
    grid.dragRows && columnModel.push({
      action: 'checkbox',
      field: `action-${drag.name}`,
      pinned: 'left',
      // cellRenderer: drag.template,
      cellRendererFramework: grid.$root.constructor.extend({
        template: renderComponent(drag.template)
      }),
      width: 13,
      headerName: '_',
      suppressSorting: true,
      suppressSizeToFit: true
    })

    grid.checkboxes || columnModel.push({
      action: true,
      // pinned: 'right',
      width: 10,
      headerName: '_',
      suppressSorting: true,
      suppressSizeToFit: true
    })

    // fixed (pinned) row selection
    grid.checkboxes && columnModel.push({
      action: grid.dragRows ? true : 'checkbox',
      pinned: 'left',
      width: 30,
      cellRendererFramework: grid.$root.constructor.extend(checkboxProxy),
      headerName: '_',
      suppressSorting: true,
      suppressSizeToFit: true
    })

    model && Object.keys(model).forEach((key, i) => {
      const column = key.substr(0, 2) !== '$$' && model[key]
      if (column) {
        const label  = model[key] && model[key].label || model[key]
        const columnWidth = grid && grid.fitColumns ? column.width : column.width > 200 ? column.width / 2 : column.width
        const _key   = key.replace('*', '')

        uid = key.charAt(0) === '*' ? _key : uid
        const colType = DataTypes.parseType(column, key)

        let isDetailsColumn = column.label === '+'
        columnModel.push(
          {
            tooltipField: grid.noTooltipFields && grid.noTooltipFields.includes(key) ? null : key,
            uid : column.uid || columnModel[0] && columnModel[0].field,
            headerName       : label,

            suppressSorting  : typeof column.sortable !== 'undefined' ? !column.sortable : isDetailsColumn,
            suppressSizeToFit: column.fixed || isDetailsColumn,
            action           : isDetailsColumn,

            width        : columnWidth ||
                            (
                              i === 1
                                ? isDetailsColumn ? 1 : 330
                                : isDetailsColumn ? 1 : 200
                            ),
            field        : _key,
            hide         : typeof column.visible !== 'undefined' && !column.visible || label === '_',
            // cellRenderer : ~label.indexOf('Date') ? DataTypes.date.column : null,
            // editable     : !column.details && column.editable,
            notEditableIf: column.notEditableIf,
            columnEditable: column.editable,
            editable : function (params) {
              if (
                column.notEditableIf &&
                column.notEditableIf.f &&
                params.node.data[column.notEditableIf.f]
              ) return

              if (
                params.node.rowPinned === 'bottom' ||
                params.colDef.options ||
                params.node.data._disabled ||
                isDetailsColumn
              ) return

              if (grid.editable === false) return false

              return grid.editable === true
                  ? true
                  : column.editable === true ||
                    params.node.data._new ||
                    params.node.data.draft
            },
            cellClassRules: {
              'disabled': function(params) {
                return (
                  column.notEditableIf && column.notEditableIf.f
                  ? params.data[column.notEditableIf.f]
                  : false
                ) || column.editable === false || params.data && params.data._disabled === true
              },
              'editable': function(params) {
                return (
                  column.notEditableIf && column.notEditableIf.f
                  ? !params.data[column.notEditableIf.f]
                  : true
                ) && column.editable === true && params.data && params.data._disabled !== true ||
                grid.editable === true && !isDetailsColumn
              }
            },
            filterFramework       : DataTypes.parseType(column).filter,
            // cellRenderer          : column.uri ? goto.template : null,
            cellRendererFramework : (column.render ? grid.$root.constructor.extend(column.render) : null) ||
              (column.details
                ? DataTypes.details
                : DataTypes.parseType(column).cell
              ) ||
              (column.uri
                ? grid.$root.constructor.extend(
                  {
                    template: renderComponent(goto.template)
                  })
                : null
              ),
            cellRendererParams    : column.details
              ? {
                comps: column.detailsComp || grid.rowDetailsComponent,
                detailsCondition: typeof (column.detailsCondition) === 'undefined'
                  ? () => true
                  : column.detailsCondition
              }
              : null,

            hasDetails            : column.details,
            detailsParamsURL: column.detailsParamsURL,
            detailsRender: column.detailsRender,
            // valueFormatter        : p => p.value || label,

            filterParams : {
              applyButton   : true,
              clearButton   : true,
              newRowsAction : 'keep'
            },
            nowrap       : column.nowrap,
            source       : column.source,
            uri          : column.uri,
            tooltip      : column.tooltip,
            onclick      : column.onclick,
            options      : column.options,
            valueFormatter : p => p.node.rowPinned === 'bottom' ? grid.totalsData[p.colDef.field] : typeof column.compose === 'function' ? column.compose(p) : p.value,
            f            : column.f,
            valueGetter  : column.getter || column.f,
            className    : colType.type || column.type,
            cellClass    : colType.type || column.type,
            selectedOptions: column.selectedOptions,
            trackBy: column.trackBy,
            validSum : column.validSum,
            currency: column.currency,
            minTotalThreshold : column.minTotalThreshold,
            maxTotalThreshold : column.maxTotalThreshold,
            minThreshold : column.minThreshold,
            maxThreshold : column.maxThreshold,
            maxLength    : column.maxLength,
            validationCol: column.validationCol,
            infoMessage : params => {
              return typeof column.infoMessage === 'function'
                ? column.infoMessage(params)
                : column.infoMessage
            },
            infoMessageIcon: column.infoMessageIcon,
            showDefaultEmpty: column.showDefaultEmpty,
            alias: column.alias,
            useSupplementalPrecision: column.useSupplementalPrecision || false,
            decimals: column.decimals
          }
        )
      }
    })

    uid = uid || columnModel[0].field

    if (model.$$view) {
      columnModel.push({
        uid,
        uri: model.$$view.uri,
        action: true,
        cellClass: 'hover-action action-view',
        cellRenderer: view.template,
        width: 100,
        headerName: '_',
        label: model.$$view.label,
        suppressSorting: true,
        suppressSizeToFit: true
      })
    }

    let lastColumnEditable =  model[Object.keys(model).slice(-1)] && model[Object.keys(model).slice(-1)].editable

    if (actions && actions.length) {
      lastColumnEditable && columnModel.push({
        action: true,
        // pinned: 'right',
        width: 60,
        headerName: '_',
        suppressSorting: true,
        suppressSizeToFit: true
      })

      const actionsExceptExpand = actions.filter(action => action.name !== 'expand')
      const actionsOnlyExpand = actions.filter(action => action.name === 'expand')

      // generic dropdown menu container for actions
      columnModel.push({
        uid,
        action: true,
        field: `more-actions`,
        headerName: '_',
        suppressSorting: true,
        suppressSizeToFit: true,
        width: 1,
        cellRendererFramework: grid.$root.constructor.extend(MoreActions),
        cellRendererParams: {
          actions: actionsExceptExpand.map(action =>
            ({
              uid,
              action: true,
              field: `action-${action.name}`,
              pinned: action.name === 'expand' ? 'left' : null,
              // cellRenderer: action.template,
              cellRendererFramework: grid.$root.constructor.extend({
                template: renderComponent(action.template)
              }),
              tpl: renderComponent(action.template),
              cellRendererParams: {
                actionable: params => {
                  return action.name === 'expand' ||
                    typeof model.$$actionable === 'function' && model.$$actionable({ data: params.data, action }) ? true : null ||
                    model.$$actionable === true ||
                    typeof model.$$actionable === 'undefined' ||
                    !model.$$actionable && null
                }
              },
              headerName: '_',
              suppressSorting: true,
              suppressSizeToFit: true,
              width: action.name === 'expand' ? 35 : 1
            })
          )
        }
      })

      columnModel.push(
        ...actionsOnlyExpand.map(action =>
          ({
            uid,
            action: true,
            field: `action-${action.name}`,
            pinned: action.name === 'expand' ? 'left' : null,
            // cellRenderer: action.template,
            cellRendererFramework: grid.$root.constructor.extend({
              template: renderComponent(action.template)
            }),
            cellRendererParams: {
              actionable: params => {
                return action.name === 'expand' ||
                  typeof model.$$actionable === 'function' && model.$$actionable({ data: params.data, action }) ? true : null ||
                  model.$$actionable === true ||
                  typeof model.$$actionable === 'undefined' ||
                  !model.$$actionable && null
              }
            },
            headerName: '_',
            suppressSorting: true,
            suppressSizeToFit: true,
            width: action.name === 'expand' ? 35 : 1
          })
        )
      )
    } else {
      columnModel.push({
        action: true,
        // pinned: 'right',
        width: 40,
        headerName: '_',
        suppressSorting: true
        // suppressSizeToFit: true
      })
    }

    return columnModel

    function renderComponent(renderFn) {
      return `<span v-if='(typeof params.actionable === "function" ? params.actionable(params) : true) && !params.node.rowPinned'>
        ${
          renderFn({
            node: {
              rowPinned: false
            },
            rowIndex: 'params.rowIndex',
            colDef: {
              uid: 'params.colDef.uid',
              uri: 'params.colDef.uri',
              onclick: 'params.colDef.onclick'
            },
            data: {
              'params.colDef.uid': 'params.data && params.data[params.colDef.uid]',
              'params.colDef.uri': 'params.data && params.data[params.colDef.uri]'
            },
            value: '{{params.value}}',
            text: 'params.value'
          }, grid)
          .replace(/data-/g, ':data-')
          .replace(':data-action', 'data-action')
          .replace(/href/g, ':href')
        }
      </span>`
    }
  },

  /**
   * @param   {Object} model      - backend column model
   * @param   {Object} params     - grid state `params`
   * @returns {Array} columnModel - mapped model
   */
  header(model, params, sort) {
    const columns = []
    const sortedColumns = []

    model && Object.keys(model).forEach((key, index) => {
      const _key   = key.replace('*', '')

      // set sorting
      if (params.sortModel.length && params.sortModel[0].colId === _key || sort && sort[key]) {
        sortedColumns.push({
          column: index,
          dir: sort && sort[key] || params.sortModel[0].sort
        })
      }

      columns.push(
        {
          data: _key,
          name: '',
          searchable: true,
          orderable: true,
          search: {
            value: params.filterModel[_key] && params.filterModel[_key].filter || '',
            regex: false
          }
        }
      )
    })

    return {
      draw    : 1,
      columns : columns,
      order   : sortedColumns,
      start   : params.startRow || 0,
      length  : 100,
      search  : {
        value : params.search,
        regex : false
      }
    }
  },

  sort(model) {
    const sortModel = []

    model && Object.keys(model).forEach(key => {
      sortModel.push({ colId: key, sort: model[key] })
    })

    return sortModel
  }
}

/**
 * Remote datasource
 * @method fetch - binds `getRows` to api request
 *
 */
export const Data = {
  /**
   * @param   {Object} grid               - vue instance
   * @returns {Object} `datasource` model - grid datasource settings
   */
  fetch(grid) {
    return {
      rowCount: null,
      getRows(params) {
        //
        grid.gridOptions.api && grid.gridOptions.api.hideOverlay()
        if (!grid.savedViews.length && grid.alias) return

        const legacy = grid.$parent.$vnode.data.class ? grid.$parent.$vnode.data.class.indexOf('legacy') > -1 : grid.legacy
        var payload = null

        params.search = grid.searchTerm
        params.screen = grid.screen

        if (legacy) {
          payload = Filter.request.call(grid, params)
        } else {
          payload = Model.header(grid.model || grid.$parent.model, params) // , grid.sort)
        }

        if (grid.data) {
          //
          params.successCallback(grid.data, grid.data.length)
        } else {
          const api = grid.endpointPayloadType === 'form' ? grid.api.forms : grid.api
          const initialSort = !grid.sort ||
            !legacy && grid.sort && payload.order.length ||
            legacy && grid.sort && payload.sort
          //
          if (grid.currentViewChanged) {
            grid.currentViewChanged = false
            return
          }
          !initialSort || api[grid.endpointMethod](grid.endpoint, payload)
            .then(response => {
              if (response && response.data && typeof response.data === 'object') {
                //
                grid.pendingRequests--
                if (response.data.recordsFiltered || response.data.Count) {
                  params.successCallback(
                    response.data.data || response.data.Data,
                    response.data.recordsFiltered || response.data.Count
                  )
                  if (JSON.parse(response.data.ExtraInfo || '{}').Totals && grid.totals) {
                    grid.totalsData = JSON.parse(response.data.ExtraInfo || '{}').Totals
                    grid.gridOptions.api.setPinnedBottomRowData([grid.totalsData])
                  }
                } else {
                  params.successCallback([], 0)
                  grid.pendingRequests || grid.gridOptions.api && grid.gridOptions.api.showNoRowsOverlay()
                  if (grid.doNotShowOverlay) {
                    grid.gridOptions.api && grid.gridOptions.api.hideOverlay()
                    grid.doNotShowOverlay = false
                  }
                  grid.updateLocales()
                }

                grid.gridOptions.datasource.loaded = true

                grid.ps.update()
              } else {
                // Notification message
                // (probably session expired)
                if (typeof response.data === 'string') {
                  if (response.data.indexOf('session has expired')) {
                    grid.$store.commit('updateSessionValidity', null)
                  }
                }
              }
            })
            .catch(error => {
              console.warn(error)

              // Notification message
              global.app.$notifications.notify({
                type      : 'warning',
                icon      : 'notifications',
                message   : error.message,
                placement : { from: 'bottom', align: 'right' }
              })
            }) && grid.pendingRequests++
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/Grid/adapters.js